import { z } from "zod";

/**
 * A user's own layout of the ERP (call of 04/11), kept on the server so it follows them from one
 * computer to the other. Each key has its schema; unknown entries are dropped.
 */
export const PREFERENCE_KEYS = ["nav", "dashboard"] as const;
export type PreferenceKey = (typeof PREFERENCE_KEYS)[number];

const id = z.string().trim().min(1).max(80);

/** The sidebar: sections folded, the order of the sections and of the entries in each. */
export const navPreferencesSchema = z.object({
  collapsed: z.array(id).max(20).default([]),
  sectionOrder: z.array(id).max(20).default([]),
  itemOrder: z.record(id, z.array(id).max(40)).default({}),
});
export type NavPreferences = z.output<typeof navPreferencesSchema>;

/** A dashboard tile: its kind, its place on a 12-column grid, and its settings. */
export const dashboardWidgetSchema = z.object({
  id,
  type: id,
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0).max(500),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(40),
  /** A widget made by the assistant: what to read and how to show it (see `customWidgetSchema`). */
  config: z.record(z.string(), z.unknown()).optional(),
});
export const dashboardPreferencesSchema = z.object({
  widgets: z.array(dashboardWidgetSchema).max(40).default([]),
});
export type DashboardPreferences = z.output<typeof dashboardPreferencesSchema>;
export type DashboardWidget = z.output<typeof dashboardWidgetSchema>;

export const PREFERENCE_SCHEMAS = {
  nav: navPreferencesSchema,
  dashboard: dashboardPreferencesSchema,
} as const satisfies Record<PreferenceKey, z.ZodType>;
